/**
 * Web Push: lets the relay wake this device with a content-free "New message"
 * / "Incoming call" notification while the app is closed or in the background.
 * The push payload is just that one word — no text, no sender — so neither the
 * relay nor Apple/Google's push service learns anything from it.
 *
 * Platform rules this has to live within:
 *  - iPhone/iPad: only works for the app opened from the Home Screen
 *    (iOS/iPadOS 16.4+), never in a Safari tab.
 *  - Safari only lets `pushManager.subscribe()` (which also shows the
 *    permission prompt) run in direct response to a tap. Any slow await
 *    before it — a network fetch of the relay's key, say — and iOS no longer
 *    counts it as the tap: the subscribe is refused and the phone never gets
 *    registered, even with permission granted. So everything subscribe needs
 *    is fetched ahead of time (`prepareNotifications`), and
 *    `enableNotifications` calls subscribe first thing.
 */
import type { PushTestResponse } from "@truewave/protocol";
import { api } from "./lib/api.js";
import { fromB64Url, toB64Url } from "./lib/bytes.js";

interface Creds {
  userId: string;
  token: string;
}

export type NotificationSupport =
  /** Supported; see `Notification.permission` for where the user stands. */
  | "supported"
  /** iPhone/iPad in a Safari tab — works only once added to the Home Screen. */
  | "ios-needs-install"
  /** This browser has no Web Push at all. */
  | "unsupported";

function isIos(): boolean {
  return (
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    // iPadOS reports itself as a Mac; a touch screen gives it away.
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)
  );
}

function isStandalone(): boolean {
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true
  );
}

export function notificationSupport(): NotificationSupport {
  const hasPush = "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
  if (isIos() && !isStandalone()) return "ios-needs-install";
  return hasPush ? "supported" : "unsupported";
}

export function notificationPermission(): NotificationPermission {
  return "Notification" in window ? Notification.permission : "denied";
}

async function registration(): Promise<ServiceWorkerRegistration | null> {
  if (!("serviceWorker" in navigator)) return null;
  // `ready` never settles when no service worker is registered (e.g. `vite dev`).
  return Promise.race([
    navigator.serviceWorker.ready,
    new Promise<null>((resolve) => setTimeout(() => resolve(null), 10_000)),
  ]);
}

/** What `pushManager.subscribe` needs, fetched before the user taps. */
interface Prepared {
  reg: ServiceWorkerRegistration;
  publicKey: string;
}
let prepared: Prepared | null = null;
let preparing: Promise<Prepared> | null = null;

/**
 * Get the service worker and the relay's push key ready in advance, so a tap
 * on "Turn on" / "Register this device" can subscribe immediately (see the
 * file comment). Safe to call often; call it when a screen offering the
 * button opens.
 */
export function prepareNotifications(): Promise<Prepared> {
  if (prepared) return Promise.resolve(prepared);
  preparing ??= (async () => {
    const reg = await registration();
    if (!reg) {
      throw new Error("TrueWave's background service isn't running. Close TrueWave fully, reopen it and try again.");
    }
    const { publicKey } = await api.vapidPublicKey();
    if (!publicKey) throw new Error("Notifications aren't set up on the TrueWave server.");
    prepared = { reg, publicKey };
    return prepared;
  })().finally(() => {
    preparing = null;
  });
  return preparing;
}

async function sendToRelay(creds: Creds, sub: PushSubscription): Promise<void> {
  const json = sub.toJSON();
  if (!json.endpoint || !json.keys?.p256dh || !json.keys.auth) {
    throw new Error("The phone returned an incomplete notification registration. Please try again.");
  }
  await api.pushSubscribe(creds, {
    endpoint: json.endpoint,
    keys: { p256dh: json.keys.p256dh, auth: json.keys.auth },
  });
}

function subscribeOptions(publicKey: string): PushSubscriptionOptionsInit {
  return { userVisibleOnly: true, applicationServerKey: fromB64Url(publicKey) };
}

/** Readable text for what subscribe threw — shown on the Settings screen. */
function explain(err: unknown): Error {
  const name = (err as { name?: string }).name;
  if (name === "NotAllowedError") {
    return new Error(
      "The phone refused to register. Check Settings → Notifications → TrueWave is allowed, then tap the button again.",
    );
  }
  return err instanceof Error ? err : new Error(String(err));
}

/**
 * Ask for permission and register this device for push. Must be called
 * straight from a tap handler, with no await in between.
 */
export async function enableNotifications(creds: Creds): Promise<NotificationPermission> {
  if (notificationSupport() !== "supported") return "denied";
  let p = prepared;
  // Normally ready long before the tap; if not, this await may cost us the
  // tap on iOS — the error below then says to tap again, which will work.
  if (!p) p = await prepareNotifications();

  // First thing: subscribe. This also shows the permission prompt if needed,
  // and returns the existing subscription if there already is one.
  let sub: PushSubscription;
  try {
    sub = await p.reg.pushManager.subscribe(subscribeOptions(p.publicKey));
  } catch (err) {
    // Subscribed earlier with a different relay key: replace it.
    const existing = await p.reg.pushManager.getSubscription().catch(() => null);
    if (!existing) throw explain(err);
    await existing.unsubscribe().catch(() => undefined);
    try {
      sub = await p.reg.pushManager.subscribe(subscribeOptions(p.publicKey));
    } catch (err2) {
      throw explain(err2);
    }
  }
  await sendToRelay(creds, sub);
  return notificationPermission();
}

/**
 * On every unlock: if this device is already registered, make sure the relay
 * still has it (a relay database reset would lose it). Never subscribes —
 * iOS refuses that without a tap — so an unregistered device is left for the
 * "Register this device" button.
 */
export async function syncNotifications(creds: Creds): Promise<void> {
  if (notificationSupport() !== "supported" || notificationPermission() !== "granted") return;
  try {
    const p = await prepareNotifications();
    const sub = await p.reg.pushManager.getSubscription();
    if (!sub) return;
    const current = sub.options.applicationServerKey;
    if (current && toB64Url(new Uint8Array(current)) !== p.publicKey) return; // stale key: needs a tap
    await sendToRelay(creds, sub);
  } catch (err) {
    console.warn("couldn't refresh the push subscription", err);
  }
}

/** Whether this device holds a push subscription right now. */
export async function hasPushSubscription(): Promise<boolean> {
  if (notificationSupport() !== "supported") return false;
  try {
    const reg = await registration();
    return Boolean(await reg?.pushManager.getSubscription());
  } catch {
    return false;
  }
}

/**
 * Re-register this device and ask the relay to push a test notification to
 * it. Throws with a readable message when a step fails, so the Settings
 * screen can say exactly what's wrong.
 */
export async function sendTestNotification(creds: Creds): Promise<PushTestResponse> {
  if (notificationSupport() === "ios-needs-install") {
    throw new Error("Open TrueWave from its Home Screen icon — iPhone only allows notifications there.");
  }
  if (notificationSupport() !== "supported") throw new Error("This browser can't receive notifications.");
  if (notificationPermission() !== "granted") throw new Error("Notifications aren't allowed yet — tap Turn on first.");
  const p = await prepareNotifications();
  const sub = await p.reg.pushManager.getSubscription();
  if (!sub) throw new Error("This device isn't registered yet — tap Register this device first.");
  await sendToRelay(creds, sub);
  return api.pushTest(creds);
}

/** Take down any notification with this tag (e.g. "Incoming call" once it's answered). */
export async function closeNotifications(tag: "truewave-call" | "truewave-message"): Promise<void> {
  try {
    const reg = await registration();
    for (const n of (await reg?.getNotifications({ tag })) ?? []) n.close();
  } catch {
    /* best-effort */
  }
}

/** Stop pushes to this device (sign-out). */
export async function disableNotifications(creds: Creds): Promise<void> {
  try {
    const reg = await registration();
    const sub = await reg?.pushManager.getSubscription();
    if (!sub) return;
    await api.pushUnsubscribe(creds, sub.endpoint).catch(() => undefined);
    await sub.unsubscribe();
  } catch {
    /* best-effort */
  }
}

/**
 * A notification raised by the open app itself, for when it's running but
 * not on screen (a background desktop tab, a minimized window) — the relay
 * doesn't push in that case because the app ACKs everything straight away.
 */
export async function showLocalNotification(kind: "message" | "call"): Promise<void> {
  if (document.visibilityState === "visible" || notificationPermission() !== "granted") return;
  const reg = await registration();
  await reg
    ?.showNotification("TrueWave", {
      body: kind === "call" ? "Incoming call" : "New message",
      tag: kind === "call" ? "truewave-call" : "truewave-message",
      data: { shownAt: Date.now() },
      icon: "/icon-192.png",
      badge: "/icon-192.png",
    })
    .catch(() => undefined);
}
